import React, { useCallback, useEffect, useState } from "react";
import { useApi } from "../utils/Api";
import SEO from "../components/SEO";
import ErrorModal from "../components/ErrorModal";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ChevronDown,
  Maximize, 
  Weight as WeightIcon, 
  Layers, 
  Copy, 
  PlusCircle, 
  User, 
  CheckCircle2, 
  Info,
  Box
} from "lucide-react";

export default function Printing() {
  const { apiFetch } = useApi();
  const [prints, setPrints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [form, setForm] = useState(() => {
    const saved = localStorage.getItem("kinews_print_form");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return { name: "", email: "", phone: "" };
      }
    }
    return { name: "", email: "", phone: "" };
  });

  useEffect(() => {
    localStorage.setItem("kinews_print_form", JSON.stringify(form));
  }, [form]);

  const [submitting, setSubmitting] = useState(false);
  const [errorModal, setErrorModal] = useState({
    open: false,
    message: "",
  });

  const [isCatDropdownOpen, setIsCatDropdownOpen] = useState(false);

  const [selection, setSelection] = useState({
    category: "",
    category_id: "",
    subLabel: "",
    group_label: "",
    qty: "",
    sided: "single",
    extras: [],
  });

  const groupData = useCallback((data) => {
    const categories = {};
    data.forEach((p) => {
      const catId = p.category_id || 0;
      if (!categories[catId]) {
        categories[catId] = { 
          id: catId, 
          name: p.category, 
          metric_name: p.metric_name || "Weight (GSM)",
          subGroups: {} 
        };
      }
      // If no size is provided, use the GSM as the primary variation label
      const subLabel = p.size && p.size.trim() !== "" ? p.size : p.group_label;
      if (!categories[catId].subGroups[subLabel]) {
        categories[catId].subGroups[subLabel] = {
          label: subLabel,
          category: p.category,
          category_id: catId,
          metric_name: p.metric_name || "Weight (GSM)",
          size: p.size,
          groups: {},
          allExtras: p.extras || [],
        };
      }
      const target = categories[catId].subGroups[subLabel];
      if (!target.groups[p.group_label]) target.groups[p.group_label] = [];
      target.groups[p.group_label].push(p);
    });
    return Object.values(categories).sort(
      (a, b) => Number(a.id) - Number(b.id),
    );
  }, []);

  const loadPrints = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiFetch("get_prints");
      if (res?.success) {
        const rawData = Array.isArray(res.data) ? res.data : [];
        setPrints(rawData);
        const grouped = groupData(rawData);
        if (grouped.length > 0) {
          const firstCat = grouped[0];
          const firstSub = Object.values(firstCat.subGroups)[0];
          const firstGroup = Object.keys(firstSub.groups)[0];
          setSelection({
            category: firstCat.name,
            category_id: firstCat.id,
            subLabel: firstSub.label,
            group_label: firstGroup,
            qty: firstSub.groups[firstGroup][0].qty,
            sided: "single",
            extras: [],
          });
        }
      } else {
        setError(res?.message || "Failed to load services.");
      }
    } catch (err) {
      setError("Connection error.");
    } finally {
      setLoading(false);
    }
  }, [apiFetch, groupData]);

  useEffect(() => {
    loadPrints();
  }, [loadPrints]);

  const grouped = groupData(prints);
  const selectedCategory = grouped.find(
    (c) => Number(c.id) === Number(selection.category_id),
  );
  const selectedSub = selectedCategory
    ? Object.values(selectedCategory.subGroups).find(
        (s) => s.label === selection.subLabel,
      )
    : null;

  const handleCategoryChange = (cat) => {
    const firstSub = Object.values(cat.subGroups)[0];
    const firstGroup = Object.keys(firstSub.groups)[0];
    setSelection({
      category: cat.name,
      category_id: cat.id,
      subLabel: firstSub.label,
      group_label: firstGroup,
      qty: firstSub.groups[firstGroup][0].qty,
      sided: "single",
      extras: [],
    });
  };

  const handleSubChange = (subLabel) => {
    const sub = Object.values(selectedCategory.subGroups).find(
      (s) => s.label === subLabel,
    );
    const firstGroup = Object.keys(sub.groups)[0];
    setSelection((prev) => ({
      ...prev,
      subLabel: sub.label,
      group_label: firstGroup,
      qty: sub.groups[firstGroup][0].qty,
      extras: [],
    }));
  };

  const toggleExtra = (extra) => {
    setSelection((prev) => {
      const exists = prev.extras.find((e) => e.name === extra.name);
      if (exists)
        return {
          ...prev,
          extras: prev.extras.filter((e) => e.name !== extra.name),
        };
      return { ...prev, extras: [...prev.extras, extra] };
    });
  };

  const handleCheckout = async () => {
    if (!form.name || !form.email || !form.phone) {
      setErrorModal({
        open: true,
        message: "Please fill in your details.",
      });
      return;
    }

    try {
      setSubmitting(true);
      const res = await apiFetch("create_print_checkout_session", {
        category_id: selection.category_id,
        subLabel: selection.subLabel,
        group_label: selection.group_label,
        qty: selection.qty,
        sided: selection.sided,
        extras: selection.extras,
        fullName: form.name,
        email: form.email,
        phone: form.phone
      });

      if (res?.success && res?.checkoutUrl) {
        window.location.href = res.checkoutUrl;
      } else {
        setErrorModal({
          open: true,
          message: res?.message || "Failed to start checkout session.",
        });
      }
    } catch (err) {
      console.error(err);
      setErrorModal({
        open: true,
        message: "An error occurred. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const currentBundle = selectedSub?.groups[selection.group_label]?.find(
    (b) => Number(b.qty) === Number(selection.qty),
  );
  const basePrice =
    selection.sided === "single"
      ? currentBundle?.price_single || 0
      : currentBundle?.price_double || 0;
  const extrasTotal = selection.extras.reduce(
    (sum, e) => sum + Number(e.price),
    0,
  );
  const totalPrice = basePrice + extrasTotal;

  return (
    <>
      <SEO
        title="Prints - Kinews Studio Limited"
        description="Premium Printing Configurator."
        url={window.location.href}
      />
      <main className="min-h-screen bg-slate-50 text-black">
        {/* HERO */}
        <section className="relative overflow-hidden bg-[#050505] text-white">
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <div className="absolute -left-20 -top-20 h-64 w-64 rounded-full bg-pink-500/15 blur-[100px] md:-left-[10%] md:-top-[10%] md:h-[40rem] md:w-[40rem]" />
            <div className="absolute -right-20 top-[20%] h-64 w-64 rounded-full bg-pink-600/10 blur-[100px] md:-right-[10%] md:h-[35rem] md:w-[35rem]" />
            <div className="absolute left-1/2 top-1/2 h-80 w-80 -translate-x-1/2 -translate-y-1/2 rounded-full bg-pink-500/5 blur-[120px] md:h-[50rem] md:w-[50rem]" />
          </div>
          <div className="relative mx-auto max-w-7xl px-5 pb-14 pt-28 md:pb-24 md:pt-40">
            <h1 className="mt-3 text-4xl font-bold tracking-tighter md:text-5xl lg:text-6xl">
              Prints
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-white/60 md:text-xl">
              High-quality professional printing services for all your business and personal needs.
            </p>
          </div>
        </section>

        <section className="relative bg-slate-50/50 px-5 pb-12 pt-6">
          <div className="mx-auto max-w-7xl">
            {loading ? (
              <div className="flex h-60 items-center justify-center bg-white rounded-[3rem] shadow-sm">
                <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-pink-500"></div>
              </div>
            ) : error ? (
              <div className="rounded-[3rem] bg-white p-20 text-center shadow-sm">
                <p className="text-xl font-bold text-slate-300">{error}</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid gap-6 lg:grid-cols-12">
                  {/* VERTICAL CATEGORY SIDEBAR */}
                  <div className="lg:col-span-3 min-w-0">
                    {/* CUSTOM MOBILE DROPDOWN SELECTOR */}
                    <div className="lg:hidden sticky top-16 z-20 bg-slate-50/80 backdrop-blur-xl py-4 border-b border-slate-200/60 min-w-0">
                      <div className="space-y-1 relative min-w-0">
                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 block truncate">
                          Select Printing Category
                        </label>
                        
                        <button
                          onClick={() => setIsCatDropdownOpen(!isCatDropdownOpen)}
                          className="flex w-full items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-3.5 text-left text-sm font-bold text-slate-900 shadow-sm outline-none transition-all active:scale-[0.98] min-w-0"
                        >
                          <span className="flex-1 min-w-0 pr-4 leading-tight break-words">
                            {selectedCategory?.name || "Select Category"}
                          </span>
                          <ChevronDown 
                            size={18} 
                            className={`text-slate-400 transition-transform duration-300 ${isCatDropdownOpen ? "rotate-180" : ""}`} 
                          />
                        </button>

                        <AnimatePresence>
                          {isCatDropdownOpen && (
                            <>
                              {/* Backdrop to close */}
                              <div 
                                className="fixed inset-0 z-10" 
                                onClick={() => setIsCatDropdownOpen(false)}
                              />
                              <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                className="absolute left-0 right-0 top-full z-20 mt-2 max-h-[60vh] overflow-y-auto rounded-[2rem] border border-slate-200 bg-white p-1.5 shadow-2xl shadow-slate-900/10 no-scrollbar min-w-0"
                              >
                                {grouped.map((cat) => {
                                  const isActive = Number(selection.category_id) === Number(cat.id);
                                  return (
                                    <button
                                      key={cat.id}
                                      onClick={() => {
                                        handleCategoryChange(cat);
                                        setIsCatDropdownOpen(false);
                                      }}
                                      className={`flex w-full items-center gap-3 rounded-[1.4rem] px-4 py-3.5 text-left text-sm transition-colors ${
                                        isActive 
                                          ? "bg-pink-50 text-pink-600" 
                                          : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                                      }`}
                                    >
                                      <div className={`h-2 w-2 shrink-0 rounded-full ${isActive ? "bg-pink-500" : "bg-transparent"}`} />
                                      <span className={`font-bold leading-tight break-words pr-2 ${isActive ? "text-pink-600" : "text-slate-700"}`}>
                                        {cat.name}
                                      </span>
                                    </button>
                                  );
                                })}
                              </motion.div>
                            </>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>

                    {/* DESKTOP VERTICAL SIDEBAR */}
                    <div className="hidden lg:block sticky top-32 z-20 self-start h-fit">
                      <div className="flex flex-col gap-1">
                        {grouped.map((cat) => {
                          const isActive =
                            Number(selection.category_id) === Number(cat.id);
                          return (
                            <button
                              key={cat.id}
                              onClick={() => handleCategoryChange(cat)}
                              className={`group relative flex items-center gap-3 rounded-2xl px-4 py-3.5 text-left transition-all duration-200 ${
                                isActive
                                  ? "bg-white shadow-md shadow-pink-500/10 border border-pink-100"
                                  : "hover:bg-white/70 border border-transparent"
                              }`}
                            >
                              <div
                                className={`absolute left-0 top-3 bottom-3 w-[3px] rounded-full transition-all duration-200 ${
                                  isActive
                                    ? "bg-pink-500"
                                    : "bg-transparent group-hover:bg-slate-200"
                                }`}
                              />
                              <span
                                className={`text-xs font-black uppercase leading-tight tracking-wider transition-colors ${
                                  isActive
                                    ? "text-slate-900"
                                    : "text-slate-500 group-hover:text-slate-700"
                                }`}
                              >
                                {cat.name}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* MAIN CONFIGURATION AREA */}
                  <div className="lg:col-span-6 min-w-0">
                    <div className="space-y-4">
                      <div className="space-y-4">
                        {/* 1. SELECT SIZE / OPTION */}
                        <div className="group rounded-[2.5rem] border border-slate-200/60 bg-white p-0.5 shadow-sm transition-all hover:shadow-xl hover:shadow-pink-500/5">
                          <div className="rounded-[2.2rem] bg-slate-50/50 p-4 md:p-6">
                            <div className="space-y-5">
                              <div className="flex items-start justify-between">
                                <div className="space-y-1 w-full min-w-0">
                                  <div className="flex flex-col sm:flex-row items-center sm:items-start gap-3">
                                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-pink-500/10 text-pink-600">
                                      <Maximize size={18} strokeWidth={2.5} />
                                    </div>
                                    <h3 className="text-base font-black uppercase tracking-wide text-slate-900 w-full text-center sm:text-left whitespace-normal">
                                      Select Size / Option
                                    </h3>
                                  </div>
                                  <p className="sm:pl-[52px] text-sm font-bold text-slate-400 text-center sm:text-left">Choose the option for your print</p>
                                </div>

                              </div>
                              <div className="flex flex-wrap gap-1 md:gap-4">
                                {selectedCategory &&
                                  Object.values(selectedCategory.subGroups).map(
                                    (sub) => {
                                      const isActive = selection.subLabel === sub.label;
                                      return (
                                        <button
                                          key={sub.label}
                                          onClick={() => handleSubChange(sub.label)}
                                          className={`group relative flex flex-col sm:flex-row flex-1 min-w-[90px] items-center justify-center gap-1 sm:gap-2 rounded-2xl border-2 px-3 py-3 transition-all duration-300 ${isActive ? "border-pink-500 bg-white shadow-xl shadow-pink-500/10" : "border-transparent bg-white/50 hover:border-slate-200 hover:bg-white"}`}
                                        >
                                          <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-all ${isActive ? "bg-pink-500 text-white" : "bg-slate-100 text-slate-400 group-hover:bg-slate-200"}`}>
                                            <Maximize size={18} strokeWidth={isActive ? 2.5 : 2} />
                                          </div>
                                          <span className={`text-sm font-black uppercase tracking-wide break-words leading-tight text-center sm:text-left ${isActive ? "text-slate-900" : "text-slate-500"}`}>
                                            {sub.label}
                                          </span>
                                          {isActive && (
                                            <div className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-pink-500 text-white shadow-lg">
                                              <CheckCircle2 size={12} strokeWidth={3} />
                                            </div>
                                          )}
                                        </button>
                                      );
                                    },
                                  )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* 2. SELECT TYPE */}
                        {selectedSub && Object.keys(selectedSub.groups).length > 0 && 
                         Object.keys(selectedSub.groups)[0] !== "null" && 
                         Object.keys(selectedSub.groups)[0] !== "" && (
                          <div className="group rounded-[2.5rem] border border-slate-200/60 bg-white p-0.5 shadow-sm transition-all hover:shadow-xl hover:shadow-pink-500/5">
                            <div className="rounded-[2.2rem] bg-slate-50/50 p-4 md:p-6">
                              <div className="space-y-5">
                                <div className="flex items-start justify-between">
                                  <div className="space-y-1 w-full min-w-0">
                                    <div className="flex flex-col sm:flex-row items-center sm:items-start gap-3">
                                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-pink-500/10 text-pink-600">
                                        <WeightIcon size={18} strokeWidth={2.5} />
                                      </div>
                                      <h3 className="text-base font-black uppercase tracking-wide text-slate-900 w-full text-center sm:text-left whitespace-normal">
                                        Select Type
                                      </h3>
                                    </div>
                                    <p className="sm:pl-[52px] text-sm font-bold text-slate-400 text-center sm:text-left">Choose the variation type for your print</p>
                                  </div>

                                </div>

                                <div className="flex flex-wrap gap-1 md:gap-4">
                                  {Object.keys(selectedSub.groups).map((group_label) => (
                                    <button
                                      key={group_label}
                                      onClick={() =>
                                        setSelection((prev) => ({
                                          ...prev,
                                          group_label,
                                          qty: selectedSub.groups[group_label][0].qty,
                                        }))
                                      }
                                      className={`group relative flex flex-col sm:flex-row flex-1 min-w-[90px] items-center justify-center gap-1 sm:gap-2 rounded-2xl border-2 px-3 py-3 transition-all duration-300 ${selection.group_label === group_label ? "border-pink-500 bg-white shadow-lg shadow-pink-500/5" : "border-transparent bg-white/50 hover:border-slate-200 hover:bg-white"}`}
                                    >
                                      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-all ${selection.group_label === group_label ? "bg-pink-500 text-white" : "bg-slate-100 text-slate-400 group-hover:bg-slate-200"}`}>
                                        <Layers size={18} strokeWidth={2} />
                                      </div>
                                      <span className={`text-sm font-black uppercase tracking-wide break-words leading-tight text-center sm:text-left ${selection.group_label === group_label ? "text-slate-900" : "text-slate-500"}`}>
                                        {group_label}
                                      </span>
                                    </button>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}



                          {/* 3. PRINT SIDES */}
                          {currentBundle?.price_double !== null && (
                            <div className="group rounded-[2.5rem] border border-slate-200/60 bg-white p-0.5 shadow-sm transition-all hover:shadow-xl hover:shadow-pink-500/5">
                              <div className="rounded-[2.2rem] bg-slate-50/50 p-4 md:p-6">
                                <div className="space-y-5">
                                  <div className="flex items-start justify-between">
                                    <div className="space-y-1 w-full min-w-0">
                                      <div className="flex flex-col sm:flex-row items-center sm:items-start gap-3">
                                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-pink-500/10 text-pink-600">
                                          <Copy size={18} strokeWidth={2.5} />
                                        </div>
                                        <h3 className="text-base font-black uppercase tracking-wide text-slate-900 w-full text-center sm:text-left whitespace-normal">
                                          Print Sides
                                        </h3>
                                      </div>
                                      <p className="sm:pl-[52px] text-sm font-bold text-slate-400 text-center sm:text-left">Choose single or double sided printing</p>
                                    </div>

                                  </div>

                                  <div className="flex w-full rounded-2xl bg-slate-200/50 p-1.5 border border-slate-200/50">
                                    <button
                                      onClick={() =>
                                        setSelection((prev) => ({
                                          ...prev,
                                          sided: "single",
                                        }))
                                      }
                                      className={`flex-1 rounded-xl py-2 text-sm font-black uppercase tracking-wide transition-all ${selection.sided === "single" ? "bg-white text-pink-600 shadow-lg shadow-slate-900/5" : "text-slate-500 hover:text-slate-900"}`}
                                    >
                                      Single
                                    </button>
                                    <button
                                      onClick={() =>
                                        setSelection((prev) => ({
                                          ...prev,
                                          sided: "double",
                                        }))
                                      }
                                      className={`flex-1 rounded-xl py-2 text-sm font-black uppercase tracking-wide transition-all ${selection.sided === "double" ? "bg-white text-pink-600 shadow-lg shadow-slate-900/5" : "text-slate-500 hover:text-slate-900"}`}
                                    >
                                      Double
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* 4. QUANTITIES */}
                          <div className="group rounded-[2.5rem] border border-slate-200/60 bg-white p-0.5 shadow-sm transition-all hover:shadow-xl hover:shadow-pink-500/5">
                            <div className="rounded-[2.2rem] bg-slate-50/50 p-4 md:p-6">
                              <div className="space-y-5">
                                <div className="flex items-start justify-between">
                                  <div className="space-y-1 w-full min-w-0">
                                    <div className="flex flex-col sm:flex-row items-center sm:items-start gap-3">
                                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-pink-500/10 text-pink-600">
                                        <Box size={18} strokeWidth={2.5} />
                                      </div>
                                      <h3 className="text-base font-black uppercase tracking-wide text-slate-900 w-full text-center sm:text-left whitespace-normal">
                                        Select Quantity
                                      </h3>
                                    </div>
                                    <p className="sm:pl-[52px] text-sm font-bold text-slate-400 text-center sm:text-left">Select the number of units to print</p>
                                  </div>

                                </div>

                                <div className="flex flex-wrap gap-1 sm:gap-2 md:gap-4">
                                  {selectedSub &&
                                    selectedSub.groups[selection.group_label]?.map(
                                      (bundle, idx) => (
                                        <button
                                          key={idx}
                                          onClick={() =>
                                            setSelection((prev) => ({
                                              ...prev,
                                              qty: bundle.qty,
                                            }))
                                          }
                                          className={`group relative flex flex-none min-w-[80px] items-center justify-center rounded-2xl border-2 py-2.5 px-2 transition-all duration-300 ${Number(selection.qty) === Number(bundle.qty) ? "border-pink-500 bg-white shadow-xl shadow-pink-500/10" : "border-transparent bg-white/50 hover:border-slate-200 hover:bg-white"}`}
                                        >
                                          <span className={`text-2xl font-black ${Number(selection.qty) === Number(bundle.qty) ? "text-pink-600" : "text-slate-300 group-hover:text-slate-400"}`}>
                                            {bundle.qty}
                                          </span>
                                        </button>
                                      ),
                                    )}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* 4. EXTRAS */}
                          <AnimatePresence mode="wait">
                            {selectedSub?.allExtras.length > 0 && (
                              <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="group rounded-[2.5rem] border border-slate-200/60 bg-white p-0.5 shadow-sm transition-all hover:shadow-xl hover:shadow-pink-500/5"
                              >
                                <div className="rounded-[2.2rem] bg-slate-50/50 p-4 md:p-6">
                                  <div className="space-y-5">
                                    <div className="flex items-start justify-between">
                                      <div className="space-y-1 w-full min-w-0">
                                          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-3">
                                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-pink-500/10 text-pink-600">
                                              <PlusCircle size={18} strokeWidth={2.5} />
                                            </div>
                                            <h3 className="text-base font-black uppercase tracking-wide text-slate-900 w-full text-center sm:text-left whitespace-normal">
                                              Add-ons
                                            </h3>
                                          </div>
                                          <p className="sm:pl-[52px] text-sm font-bold text-slate-400 text-center sm:text-left">Enhance your prints with optional extras</p>
                                      </div>

                                    </div>

                                    <div className="flex flex-col divide-y divide-slate-100">
                                      {selectedSub.allExtras.map((extra, idx) => {
                                        const isSelected = selection.extras.find(
                                          (e) => e.name === extra.name,
                                        );
                                        return (
                                          <button
                                            key={idx}
                                            onClick={() => toggleExtra(extra)}
                                            className="group flex items-center gap-4 py-4 text-left transition-colors first:pt-0 last:pb-0"
                                          >
                                            {/* Checkbox */}
                                            <div
                                              className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 transition-all duration-200 ${
                                                isSelected
                                                  ? "border-pink-500 bg-pink-500"
                                                  : "border-slate-300 bg-white group-hover:border-pink-300"
                                              }`}
                                            >
                                              {isSelected && (
                                                <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                                                  <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                              )}
                                            </div>
                                            {/* Name */}
                                            <span
                                              className={`flex-1 text-sm font-semibold transition-colors truncate min-w-0 ${
                                                isSelected ? "text-slate-900" : "text-slate-500 group-hover:text-slate-700"
                                              }`}
                                            >
                                              {extra.name}
                                            </span>
                                            {/* Price */}
                                            <span
                                              className={`text-sm font-bold tabular-nums transition-colors ${
                                                isSelected ? "text-pink-600" : "text-slate-400"
                                              }`}
                                            >
                                              +£{Number(extra.price).toFixed(2)}
                                            </span>
                                          </button>
                                        );
                                      })}
                                    </div>
                                  </div>
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      </div>

                    {/* STEP 2: YOUR DETAILS */}
                    <div className="mt-4 group rounded-[2.5rem] border border-slate-200/60 bg-white p-0.5 shadow-sm transition-all hover:shadow-xl hover:shadow-pink-500/5">
                      <div className="rounded-[2.2rem] bg-slate-50/50 p-4 md:p-6">
                        <div className="space-y-5">
                          <div className="flex items-start justify-between">
                            <div className="space-y-1 w-full min-w-0">
                              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-3">
                                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-pink-500/10 text-pink-600">
                                  <User size={18} strokeWidth={2.5} />
                                </div>
                                <h3 className="text-base font-black uppercase tracking-wide text-slate-900 w-full text-center sm:text-left whitespace-normal">Your details</h3>
                              </div>
                              <p className="sm:pl-[52px] text-sm font-bold text-slate-400 text-center sm:text-left">Provide your contact info so we can reach out to you.</p>
                            </div>

                          </div>

                          <div className="flex flex-col gap-4">
                            <input
                              placeholder="Full name"
                              className="w-full rounded-2xl border border-slate-200/60 bg-white px-4 py-3 text-sm font-medium text-slate-900 placeholder:text-slate-400 outline-none transition focus:border-pink-500"
                              value={form.name}
                              onChange={(e) => setForm({ ...form, name: e.target.value })}
                            />
                            <input
                              placeholder="Phone number"
                              className="w-full rounded-2xl border border-slate-200/60 bg-white px-4 py-3 text-sm font-medium text-slate-900 placeholder:text-slate-400 outline-none transition focus:border-pink-500"
                              value={form.phone}
                              onChange={(e) => setForm({ ...form, phone: e.target.value })}
                            />
                            <input
                              placeholder="Email address"
                              type="email"
                              className="w-full rounded-2xl border border-slate-200/60 bg-white px-4 py-3 text-sm font-medium text-slate-900 placeholder:text-slate-400 outline-none transition focus:border-pink-500"
                              value={form.email}
                              onChange={(e) => setForm({ ...form, email: e.target.value })}
                            />
                          </div>

                          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 rounded-[2rem] bg-pink-500/5 p-6 border border-pink-500/10 min-w-0">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-pink-500 text-white shadow-lg shadow-pink-500/20">
                              <Info size={20} strokeWidth={2.5} />
                            </div>
                            <div className="space-y-1 flex-1 min-w-0 text-center sm:text-left">
                              <p className="text-sm font-medium leading-relaxed text-pink-600/70">
                                After payment, a member of our team will contact you to request your print requirements.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ORDER SUMMARY - GLASS CARD */}
                  <div className="lg:col-span-3 min-w-0">
                    <div className="relative lg:sticky lg:top-32 z-20 self-start h-fit rounded-3xl border border-pink-500/20 bg-gradient-to-b from-[#181116] to-[#0d0b0d] p-4 min-[380px]:p-5 shadow-[0_20px_60px_rgba(0,0,0,0.35)] min-w-0">
                      <p className="text-sm uppercase tracking-wide text-pink-400/80">
                        Summary
                      </p>
                      <h3 className="mt-1 text-2xl font-semibold text-white">
                        Print overview
                      </h3>

                      <div className="mt-4 space-y-2 text-sm text-white/75">

                        <div className="flex flex-col min-[380px]:flex-row justify-between gap-1 min-[380px]:gap-4 border-b border-white/10 pb-3">
                          <span className="text-xs text-white/40 min-[380px]:text-sm min-[380px]:text-white/60 uppercase min-[380px]:normal-case tracking-wider min-[380px]:tracking-normal">Category</span>
                          <span className="text-left min-[380px]:text-right font-medium text-white truncate min-w-0 flex-1">
                            {selection.category}
                          </span>
                        </div>

                        <div className="flex flex-col min-[380px]:flex-row justify-between gap-1 min-[380px]:gap-4 border-b border-white/10 pb-3">
                          <span className="text-xs text-white/40 min-[380px]:text-sm min-[380px]:text-white/60 uppercase min-[380px]:normal-case tracking-wider min-[380px]:tracking-normal">Size / Option</span>
                          <span className="text-left min-[380px]:text-right font-medium text-white truncate min-w-0 flex-1">
                            {selection.subLabel}
                          </span>
                        </div>

                        <div className="flex flex-col min-[380px]:flex-row justify-between gap-1 min-[380px]:gap-4 border-b border-white/10 pb-3">
                          <span className="text-xs text-white/40 min-[380px]:text-sm min-[380px]:text-white/60 uppercase min-[380px]:normal-case tracking-wider min-[380px]:tracking-normal">Variation</span>
                          <span className="text-left min-[380px]:text-right font-medium text-white truncate min-w-0 flex-1">
                            {selection.group_label}
                          </span>
                        </div>

                        {currentBundle?.price_double !== null && (
                          <div className="flex flex-col min-[380px]:flex-row justify-between gap-1 min-[380px]:gap-4 border-b border-white/10 pb-3">
                            <span className="text-xs text-white/40 min-[380px]:text-sm min-[380px]:text-white/60 uppercase min-[380px]:normal-case tracking-wider min-[380px]:tracking-normal">Sides</span>
                            <span className="text-left min-[380px]:text-right font-medium text-white capitalize">
                              {selection.sided}
                            </span>
                          </div>
                        )}

                        <div className="flex flex-col min-[380px]:flex-row justify-between gap-1 min-[380px]:gap-4 border-b border-white/10 pb-3">
                          <span className="text-xs text-white/40 min-[380px]:text-sm min-[380px]:text-white/60 uppercase min-[380px]:normal-case tracking-wider min-[380px]:tracking-normal">Quantity</span>
                          <span className="text-left min-[380px]:text-right font-medium text-white">
                            {selection.qty} units
                          </span>
                        </div>

                        {selection.extras.length > 0 && (
                          <div className="space-y-3 pt-1 border-b border-white/10 pb-3">
                            <span className="block text-xs font-semibold uppercase tracking-wider text-white/40">
                              Add-ons
                            </span>
                            {selection.extras.map((ex, i) => (
                               <div
                                 key={i}
                                 className="flex flex-col min-[380px]:flex-row justify-between gap-1 min-[380px]:gap-4 animate-in fade-in slide-in-from-right-2"
                               >
                                 <span className="flex-1 min-w-0 truncate text-white/60 min-[380px]:text-white">{ex.name}</span>
                                 <span className="font-medium text-white shrink-0 text-left min-[380px]:text-right">
                                   +£{Number(ex.price).toFixed(2)}
                                 </span>
                               </div>
                            ))}
                          </div>
                        )}

                        <div className="mt-6 rounded-2xl bg-white/5 px-5 py-2 border border-white/5">
                          <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-1 sm:gap-0">
                            <span className="text-sm sm:text-base font-semibold text-white/60 sm:text-white">
                              Total
                            </span>
                            <span className="text-xl sm:text-2xl font-black text-pink-400 tabular-nums">
                              £{totalPrice.toFixed(2)}
                            </span>
                          </div>
                        </div>

                        <button
                          onClick={handleCheckout}
                          disabled={submitting}
                          className={`mt-6 w-full rounded-2xl px-6 py-4 text-base font-semibold transition-all duration-300 ${
                            submitting
                              ? "bg-slate-200 text-slate-400 cursor-not-allowed"
                              : "bg-pink-500 text-white hover:bg-pink-600 shadow-lg shadow-pink-500/25 hover:shadow-pink-500/40 active:scale-[0.98]"
                          }`}
                        >
                          {submitting ? "Redirecting..." : "Continue to Payment"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      </main>

      <ErrorModal
        open={errorModal.open}
        message={errorModal.message}
        onClose={() => setErrorModal({ ...errorModal, open: false })}
      />
    </>
  );
}
